import { api } from '@/api/client';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import {
  Badge,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Progress,
  Separator,
  toast,
} from '@kgc/ui';
import {
  Ban,
  CheckCircle,
  ChevronDown,
  Clock,
  Loader2,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import type { WorkflowExecution, WorkflowExecutionStatus } from '../types';
import { StepTimeline } from './StepTimeline';

interface StatusConfig {
  icon: LucideIcon;
  className: string;
  badgeClass: string;
  spin?: boolean | undefined;
}

const STATUS_CONFIG: Record<WorkflowExecutionStatus, StatusConfig> = {
  running: {
    icon: Loader2,
    className: 'text-blue-500',
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    spin: true,
  },
  pending: {
    icon: Clock,
    className: 'text-yellow-500',
    badgeClass: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  completed: {
    icon: CheckCircle,
    className: 'text-green-500',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  },
  failed: {
    icon: XCircle,
    className: 'text-red-500',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  },
  cancelled: {
    icon: Ban,
    className: 'text-muted-foreground',
    badgeClass: 'bg-muted text-muted-foreground',
  },
};

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('hu-HU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

interface ExecutionRowProps {
  execution: WorkflowExecution;
  onCancelled?: (() => void) | undefined;
}

export function ExecutionRow({ execution, onCancelled }: ExecutionRowProps) {
  const { t } = useTranslation();
  const config = STATUS_CONFIG[execution.status];
  const StatusIcon = config.icon;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const paramEntries = Object.entries(execution.parameters);

  async function handleCancel() {
    setIsCancelling(true);
    try {
      await api.post(`/workflows/executions/${execution.id}/cancel`);
      toast.success(t('integration.flow.execution.cancelled', { name: execution.workflowName }));
      setShowCancelDialog(false);
      onCancelled?.();
    } catch {
      toast.error(t('integration.flow.execute.error', { name: execution.workflowName }));
    } finally {
      setIsCancelling(false);
    }
  }

  return (
    <Card data-testid={`flow-execution-row-${execution.id}`}>
      <CardContent className="py-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusIcon
              className={cn('h-4 w-4', config.className, config.spin && 'animate-spin')}
              aria-hidden="true"
            />
            <span className="font-medium text-sm text-foreground">{execution.workflowName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              className={config.badgeClass}
              data-testid={`flow-execution-status-${execution.id}`}
            >
              {t(`integration.flow.status.${execution.status}`)}
            </Badge>
          </div>
        </div>

        {(execution.status === 'running' || execution.status === 'pending') && (
          <div className="flex items-center gap-2">
            <Progress value={execution.progress} className="flex-1 h-2" />
            <span className="text-xs text-muted-foreground w-10 text-right">
              {execution.progress}%
            </span>
          </div>
        )}

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {execution.currentStepIndex + 1}/{execution.steps.length}{' '}
            {t('integration.flow.running.step')}
          </span>
          <div className="flex items-center gap-2">
            <span>
              {execution.startedBy} · {formatTime(execution.startedAt)}
            </span>
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              data-testid={`flow-execution-toggle-${execution.id}`}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label={
                isExpanded
                  ? t('integration.flow.execution.hideDetails')
                  : t('integration.flow.execution.details')
              }
            >
              <ChevronDown
                className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')}
              />
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="pt-2 space-y-3" data-testid={`flow-execution-details-${execution.id}`}>
            <Separator />

            {execution.steps.length > 0 && <StepTimeline steps={execution.steps} />}

            {paramEntries.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">
                  {t('integration.flow.execution.parameters')}
                </p>
                <div className="text-xs space-y-0.5">
                  {paramEntries.map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <span className="text-muted-foreground">{key}:</span>
                      <span className="text-foreground">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(execution.status === 'running' || execution.status === 'pending') && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowCancelDialog(true)}
                data-testid={`flow-cancel-btn-${execution.id}`}
              >
                {t('integration.flow.execution.cancel')}
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('integration.flow.execution.cancelConfirm')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{execution.workflowName}</p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowCancelDialog(false)}>
              {t('integration.flow.execution.keepRunning')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => void handleCancel()}
              disabled={isCancelling}
            >
              {isCancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('integration.flow.execution.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
